import type {
  ShopifyConversionsParams,
  ShopifyConversionsRange,
  ShopifyRefusedParams,
  ShopifyStoreDiscountFilters,
} from '@discovr/types';
import type { ShopifyCouponListParams } from './hooks/useCampaignShopifyCoupons';

/**
 * Canonical React Query keys for the Shopify integration + coupon surface.
 * Single source of truth for fetches + mutation invalidations. Tuples are
 * `as const` so React Query v5 narrows the QueryKey type.
 *
 * Brand-wide keys (status, conversions) live at the top level.
 * Per-campaign keys (config, coupons, products) are nested under `campaign`.
 */
export const shopifyKeys = {
  all: ['shopify'] as const,
  staffStores: () => ['shopify', 'staff-stores'] as const,
  staffStoresPage: (page: number, limit: number) =>
    ['shopify', 'staff-stores', page, limit] as const,
  status: () => ['shopify', 'status'] as const,
  claimPreview: (token: string) => ['shopify', 'claim-preview', token] as const,
  summary: () => ['shopify', 'summary'] as const,
  glance: () => ['shopify', 'glance'] as const,
  uninstallImpact: () => ['shopify', 'uninstall-impact'] as const,
  storeDiscounts: (filters: ShopifyStoreDiscountFilters = {}) =>
    ['shopify', 'store-discounts', filters] as const,
  storeDiscountCampaigns: () => ['shopify', 'store-discount-campaigns'] as const,
  conversions: (campaignId: string) => ['shopify', 'conversions', campaignId] as const,
  conversionsPage: (campaignId: string, params: ShopifyConversionsParams) =>
    ['shopify', 'conversions', campaignId, 'page', params] as const,
  conversionsSummary: (campaignId: string, range: ShopifyConversionsRange = {}) =>
    ['shopify', 'conversions', campaignId, 'summary', range] as const,
  conversionsSeries: (campaignId: string, range: ShopifyConversionsRange = {}) =>
    ['shopify', 'conversions', campaignId, 'series', range] as const,
  duplicateSources: (campaignId: string) =>
    ['shopify', 'duplicate-sources', campaignId] as const,
  conversionsRefused: (campaignId: string, params: ShopifyRefusedParams) =>
    ['shopify', 'conversions', campaignId, 'refused', params] as const,
  campaignConfig: (campaignId: string) => ['shopify', 'campaign-config', campaignId] as const,
  /** No params → the campaign prefix, which invalidates every page and the
   *  summary. With params → one section page. */
  campaignCoupons: (campaignId: string, params?: ShopifyCouponListParams) =>
    params
      ? (['shopify', 'campaign-coupons', campaignId, params] as const)
      : (['shopify', 'campaign-coupons', campaignId] as const),
  campaignCouponsSummary: (campaignId: string) =>
    ['shopify', 'campaign-coupons', campaignId, 'summary'] as const,
  campaignProducts: (campaignId: string, q: string) =>
    ['shopify', 'campaign-products', campaignId, q] as const,
  /** Distinct from campaignProducts — an infinite query's cache shape
   *  ({pages, pageParams}) is incompatible with the plain query's ProductPage
   *  shape, so the two must never share a key. Backs the "Browse products"
   *  dialog's paginated list. */
  campaignProductsBrowse: (campaignId: string, q: string) =>
    ['shopify', 'campaign-products-browse', campaignId, q] as const,
  campaignProductVariants: (campaignId: string, productId: string) =>
    ['shopify', 'campaign-product-variants', campaignId, productId] as const,
  campaignCollections: (campaignId: string, q: string) =>
    ['shopify', 'campaign-collections', campaignId, q] as const,
  campaignCreators: (campaignId: string, q: string) =>
    ['shopify', 'campaign-creators', campaignId, q] as const,
  /** Every creator-picker search of one campaign. */
  campaignCreatorsAll: (campaignId: string) =>
    ['shopify', 'campaign-creators', campaignId] as const,
  campaignProductStatus: (campaignId: string) =>
    ['shopify', 'campaign-product-status', campaignId] as const,
} as const;
